import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../utils/api';

/* ── Types ─────────────────────────────────────────────────────────── */

export interface OrgSecurityPolicy {
  id: string | null;
  orgId: string;
  forceScreenLock: boolean;
  maxLockTimeout: number;
  employeeCanDisable: boolean;
  employeeCanReduce: boolean;
}

interface SecurityState {
  // User screen lock preferences
  screenLockEnabled: boolean;
  screenLockTimeout: number;   // minutes, 0 = disabled
  screenLockOnHide: boolean;

  // Organization policy (fetched from API)
  orgPolicy: OrgSecurityPolicy | null;
  policyLoaded: boolean;

  // Legacy fields (kept for backward compat with existing UI)
  showWarning: boolean;

  // Actions
  /** Fetch user screen lock settings + org policy from API. Call on login. */
  fetchSettings: () => Promise<void>;
  /** Save user screen lock settings to API (validated against policy). */
  saveScreenLock: (timeout: number, onHide: boolean) => Promise<void>;
  /** Update local state only (optimistic before API response). */
  setScreenLockTimeout: (minutes: number) => void;
  setScreenLockOnHide: (enabled: boolean) => void;
  setScreenLockEnabled: (enabled: boolean) => void;
  setShowWarning: (enabled: boolean) => void;
  /** Reset store on logout. */
  reset: () => void;
}

/* ── Store ─────────────────────────────────────────────────────────── */

export const useSecurityStore = create<SecurityState>()(
  persist(
    (set, get) => ({
      screenLockEnabled: true,
      screenLockTimeout: 15,
      screenLockOnHide: true,
      orgPolicy: null,
      policyLoaded: false,
      showWarning: true,

      fetchSettings: async () => {
        try {
          const [settingsRes, policyRes] = await Promise.all([
            api.get<{ screenLockTimeout: number; screenLockOnHide: boolean }>(
              '/security/screen-lock',
            ),
            api.get<OrgSecurityPolicy>('/security/policy'),
          ]);

          const settings = settingsRes.data;
          const policy = policyRes.data;

          set({
            screenLockTimeout: settings.screenLockTimeout,
            screenLockOnHide: settings.screenLockOnHide,
            screenLockEnabled: settings.screenLockTimeout > 0,
            orgPolicy: policy,
            policyLoaded: true,
          });
        } catch {
          // If API fails (e.g. user not yet registered for security),
          // fall back to defaults — don't block the UI
          set({
            orgPolicy: null,
            policyLoaded: true,
          });
        }
      },

      saveScreenLock: async (timeout: number, onHide: boolean) => {
        const { data } = await api.put<{ screenLockTimeout: number; screenLockOnHide: boolean }>(
          '/security/screen-lock',
          { screenLockTimeout: timeout, screenLockOnHide: onHide },
        );
        set({
          screenLockTimeout: data.screenLockTimeout,
          screenLockOnHide: data.screenLockOnHide,
          screenLockEnabled: data.screenLockTimeout > 0,
        });
      },

      setScreenLockTimeout: (minutes) => set({ screenLockTimeout: minutes, screenLockEnabled: minutes > 0 }),
      setScreenLockOnHide: (enabled) => set({ screenLockOnHide: enabled }),
      setScreenLockEnabled: (enabled) => {
        if (!enabled) {
          set({ screenLockEnabled: false, screenLockTimeout: 0 });
        } else {
          set({ screenLockEnabled: true, screenLockTimeout: get().screenLockTimeout || 15 });
        }
      },
      setShowWarning: (enabled) => set({ showWarning: enabled }),

      reset: () =>
        set({
          screenLockEnabled: true,
          screenLockTimeout: 15,
          screenLockOnHide: true,
          orgPolicy: null,
          policyLoaded: false,
          showWarning: true,
        }),
    }),
    {
      name: 'barmetvault-security',
      partialize: (state) => ({
        // Persist user preferences locally as a cache; API is source of truth
        screenLockTimeout: state.screenLockTimeout,
        screenLockOnHide: state.screenLockOnHide,
        screenLockEnabled: state.screenLockEnabled,
        showWarning: state.showWarning,
      }),
    },
  ),
);